import { gql } from '@apollo/client';

const FLIGHT = gql`
  fragment Flight on Flight {
    flightIcon
    numberOfStops
    totalDurationMins
    totalLayoverMins
    flightSegments {
      segmentId
      marketingAirline {
        code
        name
        icon
      }
      airplane {
        code
        name
      }
      number
      operatingAirline {
        code
        name
        icon
      }
      departure {
        airport {
          country
          city
          name
          code
        }
        time
        date
      }
      arrival {
        airport {
          country
          city
          name
          code
        }
        time
        date
      }
      durationMins
      layoverMins
    }
  }
`;

const IS_LOGGED_IN = gql`
  query IsUserLoggedIn {
    isLoggedIn
  }
`;
const BOOKING_FRAGMENT = gql`
  fragment BookingFragment on Booking {
    id
    token
    country {
      office {
        code
      }
      phoneNumber
      code
    }
    upgradesInfo {
      bookingId
      upgradeEligible
      offerUrl
    }
    guests {
      age
      airAccommodations {
        flightNumber
      }
      birthDate
      cas
      email
      firstName
      gender
      index
      lastName
      nationality
      stateCode
      travelDocument
      phoneNumber
      _mutableFields
    }
    price {
      balanceDue
      balanceDueDate
      currencyCode
      depositAmount
      depositAmountOwed
      depositDueDate
      depositPaid
      discounts {
        amount
        description
        promotion {
          title
        }
      }
      gratuities
      originalCruiseFare
      subTotal
      taxesFees
      paymentsApplied
      total
      totalBalanceDue
      travelProtection
      preTour
      flightCosts
      inFinal
      depositAmountOwed
    }
    status
    stateroom {
      accessible
      deck {
        code
        name
      }
      number
      position
      subType
      type
      image
    }
    sailing {
      sailDate
      itinerary {
        preTour {
          duration
        }
        description
        totalNights
        ship {
          code
          name
        }
        code
        name
        destination {
          code
          name
        }
        ship {
          code
          name
        }
        departurePort {
          name
          region
        }
        media {
          images {
            path
          }
        }
        days {
          number
          type
          ports {
            activity
            arrivalTime
            departureTime
            port {
              code
              name
              region
            }
          }
        }
      }
    }
    type
  }
`;
const FLIGHT_PROFILE_FRAGMENT = gql`
  fragment FlightProfile on FlightProfile {
    id
    currency
    guests {
      id
      flightGuest {
        firstName
        lastName
        allowCancellation
        allowReshop
        gdsLocators
        airChargesAmount
        airPenaltyAmount
        airFee
        airFeeAfterDueDate
        paymentDate
        refundableFlag
        transfers {
          componentType
          price
          status
          transferCode
          transferDescription
          type
        }
        cancelationPenalities {
          amount
          isFinalPayment
          isRefundable
        }
        guestFlights {
          arrivalDate
          arrivalTime
          departDate
          departTime
          flightIcon
          routeDirection
          confirmationNumbers
          totalDurationMins
          numberOfStops
          gdsLocator
          flightSegments {
            number
            operatingAirline {
              code
              name
              icon
            }
            marketingAirline {
              code
              name
              icon
            }
            cabinClass {
              name
              code
            }
            durationMins
            departure {
              airport {
                code
              }
            }
            layoverMins
            airplane {
              code
              name
            }
            arrival {
              airport {
                name
                code
                city
                country
              }
              date
              time
            }
            departure {
              airport {
                name
                code
                city
                country
              }
              date
              time
            }
          }
        }
      }
    }
  }
`;

const GUEST_AIR_PROFILE = gql`
  ${FLIGHT}
  query GuestAirProfile($input: GuestAirProfileInput) {
    guestAirProfile(input: $input) {
      correlationID
      hasFlights
      inboundFlight {
        confirmationNumbers
        ...Flight
      }
      outboundFlight {
        confirmationNumbers
        ...Flight
      }
    }
  }
`;

const RETRIEVE_GUEST_PROFILE_BOOKING = gql`
  ${BOOKING_FRAGMENT}
  ${FLIGHT_PROFILE_FRAGMENT}
  query retrieveBookingByGAProfile($accessToken: String!, $bookingId: String) {
    retrieveBookingByGAProfile(accessToken: $accessToken, bookingId: $bookingId) {
      token
      booking {
        id
        upgradesInfo {
          bookingId
          upgradeEligible
          offerUrl
        }
        ...Booking
        flightProfile {
          ...FlightProfile
        }
      }
      shortProfileBooking {
        bookingId
        lastName
        sailDate
        shipCode
        token
        endDate
        shipName
        type
      }
    }
  }
`;
const FLIGHT_PROFILE = gql`
  query flightProfile($ignoreCache: Boolean) {
    currentBooking {
      type
      id
      guests {
        lastName
      }
      sailing {
        sailDate
        itinerary {
          code
          days {
            ports {
              activity
              arrivalTime
              departureTime
              port {
                code
                name
                region
              }
            }
          }
          description
          departurePort {
            code
            name
            region
          }
          name
          ship {
            code
            name
          }
          totalNights
        }
      }
      price {
        balanceDue
        balanceDueDate
        currencyCode
        inFinal
      }
    }
    flightProfile(ignoreCache: $ignoreCache) {
      id
      currency
      sailing {
        sailTime
      }
      flightWindows {
        arrival {
          earliestDate
          latestDate
          endTime
        }
        departure {
          earliestDate
          latestDate
          startTime
        }
      }
      guests {
        email
        id
        flightGuest {
          age
          firstName
          gender
          lastName
          allowCancellation
          allowReshop
          gdsLocators
          airChargesAmount
          airPenaltyAmount
          airFee
          airFeeAfterDueDate
          paymentDate
          refundableFlag
          transfers {
            transferCode
            componentType
            status
            price
            transferDescription
            type
          }
          guestFlights {
            gdsLocator
            routeDirection
            confirmationNumbers
            totalDurationMins
            numberOfStops
            arrivalDate
            departDate
            flightIcon
            flightSegments {
              number
              durationMins
              layoverMins
              operatingAirline {
                name
              }
              cabinClass {
                code
                name
              }
              marketingAirline {
                code
              }
              arrival {
                time
                date
                airport {
                  code
                  name
                }
              }
              departure {
                time
                date
                airport {
                  code
                  name
                }
              }
            }
          }
        }
        flightPassengerType
      }
      nearByAirports {
        airportCode
        nearByAirports
      }
      nearestAirport {
        arrival {
          city
          code
          name
        }
      }
      booking {
        finalPaymentDate
      }
    }
    airports(limit: -1) {
      totalCount
      nodes {
        country
        city
        name
        code
      }
    }
  }
`;
const FLIGHT_PROFILE_EMAIL = gql`
  query flightProfile {
    flightProfile {
      guests {
        email
      }
    }
  }
`;
const SHIP_BROWSE_FLIGTH = gql`
  fragment ShipBrowseFlight on ShipBrowseFlight {
    sailing {
      finalPaymentDate
      returnDate
      returnTime
      sailDate
      sailTime
      shipCode
    }
    validDates {
      from
      to
    }
  }
`;
const MATCHING_FLIGHTS = gql`
  fragment MatchingFlights on MatchingFlight {
    arrivalDate
    arrivalTime
    departDate
    departTime
    flightIcon
    lowestAvailableFare {
      value
      currency
    }
    operatingAirlines {
      name
    }
    routeId
    totalDurationMins
    routeType {
      code
      name
    }
    flightSegments {
      availableCabins {
        mappedCabin
        selectedCabin
      }
      marketingAirline {
        code
        name
        icon
      }
      operatingAirline {
        code
        name
        icon
      }
      airplane {
        code
        name
      }
      number
      departure {
        airport {
          name
          city
          code
          country
        }
        date
        time
      }
      arrival {
        airport {
          name
          city
          code
          country
        }
        date
        time
      }
      durationMins
      layoverMins
    }
    details {
      baggageInfo {
        freeBaggageCount
        baggageAllowance {
          allowanceId
          pieceCount
          description
        }
        baggageCharges {
          chargeId
          pieceCount
          price {
            value
            currency
          }
          description
        }
      }
      cabinClass {
        code
        name
      }
      currency
      hashKey
      isRefundable
      isPaymentRequired
      inflightServices {
        key
        value
      }
      isMixedCabin
      passengerQty
      pricePerPassenger {
        value
        currency
      }
      wasPricePerPassenger {
        currency
        value
      }
      seatsAvailable
      totalPrice {
        value
        currency
      }
      wasTotalPrice {
        currency
        value
      }
    }
  }
`;
const FLIGHT_FILTERS = gql`
  fragment FlightFilters on FlightFilters {
    stops {
      nonStop
      oneStop
      twoStops
    }
    flightTimes {
      takeOff {
        minDateTime
        maxDateTime
      }
      landing {
        minDateTime
        maxDateTime
      }
    }
    minMaxPrice {
      min {
        value
        currency
      }
      max {
        value
        currency
      }
    }
    airlines {
      code
      name
    }
    classes {
      cabinClass {
        code
        name
      }
      enabled
    }
  }
`;
const CRUISE_SEARCH_FLIGTHS = gql`
  ${MATCHING_FLIGHTS}
  ${FLIGHT_FILTERS}
  query flightsSearch(
    $bookingID: String!
    $departureAirportCode: String!
    $guests: [HeldConfirmGuestInput!]
  ) {
    flightsSearch(
      input: { bookingID: $bookingID, departureAirportCode: $departureAirportCode, guests: $guests }
    ) {
      correlationID
      matchingFlights {
        ...MatchingFlights
      }
      flightFilters {
        ...FlightFilters
      }
    }
  }
`;
const FLIGHTS_SEARCH = gql`
  ${MATCHING_FLIGHTS}
  ${FLIGHT_FILTERS}
  query flightsSearch(
    $departureAirportCode: String!
    $arrivalAirportCode: String!
    $returnDepartureAirportCode: String
    $departureDate: String!
    $arrivalDate: String!
    $bookingID: String!
    $numberOfGuests: Int
    $guestIDs: [String!]
    $currency: String
    $cabinClass: String
    $mode: String
    $type: String
    $guests: [HeldConfirmGuestInput!]
  ) {
    flightsSearch(
      input: {
        departureAirportCode: $departureAirportCode
        arrivalAirportCode: $arrivalAirportCode
        returnDepartureAirportCode: $returnDepartureAirportCode
        departureDate: $departureDate
        arrivalDate: $arrivalDate
        bookingID: $bookingID
        numberOfGuests: $numberOfGuests
        guestIDs: $guestIDs
        currency: $currency
        cabinClass: $cabinClass
        mode: $mode
        type: $type
        guests: $guests
      }
    ) {
      correlationID
      matchingFlights {
        ...MatchingFlights
      }
      flightFilters {
        ...FlightFilters
      }
    }
  }
`;
const FLIGHTS_SEARCH_RETURN = gql`
  ${MATCHING_FLIGHTS}
  ${FLIGHT_FILTERS}
  query flightsSearchReturn(
    $correlationID: String!
    $hashKey: String!
    $cabinCode: String!
    $mode: String
    $guests: [HeldConfirmGuestInput!]
    $bookingID: String
  ) {
    returnFlightsSearch(
      input: {
        correlationID: $correlationID
        hashKey: $hashKey
        cabinCode: $cabinCode
        mode: $mode
        guests: $guests
        bookingID: $bookingID
      }
    ) {
      correlationID
      matchingFlights {
        ...MatchingFlights
      }
      flightFilters {
        ...FlightFilters
      }
    }
  }
`;
const GET_BROWSE_INITIAL_INFORMATION = gql`
  query GetBrowseInitialInformation(
    $from: String
    $to: String
    $includeSoldOutSailings: Boolean
    $onlyActive: Boolean
  ) {
    ships {
      code
      name
    }
    searchSailings(
      input: { from: $from, to: $to }
      includeSoldOutSailings: $includeSoldOutSailings
      onlyActive: $onlyActive
    ) {
      dateRange {
        from
        to
      }
      shipSailings {
        shipCode
        shipName
        sailingDates
      }
    }
    airports(limit: -1) {
      totalCount
      nodes {
        country
        city
        name
        code
      }
    }
  }
`;
const BROWSE_FLIGHTS_SEARCH = gql`
  ${MATCHING_FLIGHTS}
  ${FLIGHT_FILTERS}
  ${SHIP_BROWSE_FLIGTH}
  query browseFlightsSearch(
    $arrivalAirportCode: String
    $returnDepartureAirportCode: String
    $shipCode: String!
    $sailDate: String!
    $departureAirportCode: String!
    $numberOfGuests: Int!
    $departureDate: String
    $arrivalDate: String
    $type: String
  ) {
    browseFlightsSearch(
      input: {
        arrivalAirportCode: $arrivalAirportCode
        returnDepartureAirportCode: $returnDepartureAirportCode
        shipCode: $shipCode
        sailDate: $sailDate
        departureAirportCode: $departureAirportCode
        numberOfGuests: $numberOfGuests
        departureDate: $departureDate
        arrivalDate: $arrivalDate
        type: $type
      }
    ) {
      correlationID
      matchingFlights {
        ...MatchingFlights
      }
      flightFilters {
        ...FlightFilters
      }
      shipBrowseFlight {
        ...ShipBrowseFlight
      }
    }
  }
`;

const LAUNCH_DARKLY_FLAGS_AIR = gql`
  query ldFlags(
    $ldUser: String!
    $language: String
    $country: String
    $clientApp: ClientApplication
    $application: Application
    $officeID: String
    $brand: Brand
    $bookingCountry: String
  ) {
    ldFlags(
      ldUser: $ldUser
      language: $language
      country: $country
      clientApp: $clientApp
      application: $application
      officeID: $officeID
      brand: $brand
      bookingCountry: $bookingCountry
    )
  }
`;

const BOOKING = gql`
  fragment Booking on Booking {
    id
    token
    country {
      code
      phoneNumber
      office {
        code
      }
    }
    guests {
      id
      age
      airAccommodations {
        flightNumber
      }
      birthDate
      cas
      firstName
      gender
      index
      lastName
      nationality
      stateCode
      phoneNumber
      _mutableFields
    }
    price {
      balanceDue
      balanceDueDate
      currencyCode
      depositAmount
      depositDueDate
      depositPaid
      discounts {
        amount
        description
      }
      gratuities
      originalCruiseFare
      subTotal
      taxesFees
      total
      totalBalanceDue
      travelProtection
      preTour
      flightCosts
    }
    linkedBookings {
      id
      guests {
        firstName
      }
      stateroom {
        accessible
        deck {
          code
          name
        }
        number
        position
        type
      }
    }
    status
    stateroom {
      accessible
      deck {
        code
        name
      }
      number
      position
      subType
      type
    }
    sailing {
      itinerary {
        totalNights
        destination {
          name
        }
        ship {
          code
          name
        }
        media {
          images {
            path
          }
        }
        days {
          number
          type
          ports {
            activity
            arrivalTime
            departureTime
            port {
              code
              name
              region
            }
          }
        }
        departurePort {
          name
          region
        }
      }
      sailDate
    }
    type
    flightProfile {
      id
      currency
      guests {
        email
        flightGuest {
          firstName
          lastName
          guestFlights {
            arrivalDate
            arrivalTime
            departDate
            departTime
            flightIcon
            routeDirection
            confirmationNumbers
            totalDurationMins
            numberOfStops
            flightSegments {
              number
              operatingAirline {
                code
                name
                icon
              }
              marketingAirline {
                code
                name
                icon
              }
              cabinClass {
                name
                code
              }
              durationMins
              departure {
                airport {
                  code
                }
              }
              arrival {
                airport {
                  code
                }
              }
            }
          }
        }
      }
    }
  }
`;
const GIFT_CARD_BALANCE_INQUIRY = gql`
  query giftCardBalanceInquiry($accountNumber: String!, $cvv2: String!, $expirationDate: String!) {
    giftCardBalanceInquiry(
      input: { accountNumber: $accountNumber, cvv2: $cvv2, expirationDate: $expirationDate }
    ) {
      accountNumber
      price {
        value
        currency
      }
      balanceResponse
      status
    }
  }
`;
const RETRIEVE_BOOKING = gql`
  ${BOOKING}
  query retrieveBooking(
    $bookingId: String!
    $lastName: String!
    $shipCode: String!
    $sailDate: String!
  ) {
    retrieveBooking(
      input: {
        bookingId: $bookingId
        lastName: $lastName
        shipCode: $shipCode
        sailDate: $sailDate
        application: AIR
      }
    ) {
      token
      booking {
        ...Booking
      }
    }
  }
`;

const RETRIEVE_BOOKING_BY_PROFILE = gql`
  ${BOOKING}
  query RetrieveBookingsByProfile($accessToken: String!) {
    retrieveBookingsByProfile(accessToken: $accessToken) {
      ...Booking
    }
  }
`;

const CURRENT_BOOKING = gql`
  ${BOOKING}
  query currentBooking {
    currentBooking {
      ...Booking
    }
  }
`;

const LITE_PROFILE = gql`
  query LiteProfile {
    getLiteProfile {
      loyaltyData
      creationDate
      loyaltyPoints
      loyaltyTier
    }
  }
`;

const SEARCH_PACKAGE_BY_CRUISE = gql`
  query searchPackageCodeByCruiseInfo($sailDate: String!, $shipCode: String!) {
    searchPackageCodeByCruiseInfo(sailDate: $sailDate, shipCode: $shipCode)
  }
`;

const SEARCH_ITINERARY_BY_BOOKING_INFO = gql`
  query SearchSailingByBookingInfo($sailDate: String!, $shipCode: String!) {
    searchSailingByBookingInfo(sailDate: $sailDate, shipCode: $shipCode) {
      name
      totalNights
    }
  }
`;

const GET_CRUISE = gql`
  query Cruise($cruiseId: String, $filters: String) {
    cruise(id: $cruiseId, filters: $filters) {
      id
      sailings {
        itinerary {
          type
          name
          totalNights
          departurePort {
            name
            code
            region
          }
        }
      }
    }
  }
`;

export {
  CURRENT_BOOKING,
  IS_LOGGED_IN,
  FLIGHT_PROFILE,
  CRUISE_SEARCH_FLIGTHS,
  FLIGHT_PROFILE_EMAIL,
  FLIGHTS_SEARCH,
  FLIGHTS_SEARCH_RETURN,
  GET_BROWSE_INITIAL_INFORMATION,
  BROWSE_FLIGHTS_SEARCH,
  LAUNCH_DARKLY_FLAGS_AIR,
  BOOKING,
  GIFT_CARD_BALANCE_INQUIRY,
  RETRIEVE_BOOKING,
  LITE_PROFILE,
  RETRIEVE_GUEST_PROFILE_BOOKING,
  RETRIEVE_BOOKING_BY_PROFILE,
  SEARCH_PACKAGE_BY_CRUISE,
  SEARCH_ITINERARY_BY_BOOKING_INFO,
  GET_CRUISE,
  GUEST_AIR_PROFILE,
  FLIGHT,
};
